<template>
    <form
        class="mt-5 grid-cols-layout pb-5 lg:grid"
        @submit.prevent="updateCustomStatuses"
    >
        <div class="mb-6 lg:mb-0 lg:mt-4">
            <h3
                class="mb-[5px] text-lg font-medium leading-normal tracking-small text-gray-800"
            >
                Custom Template Status
            </h3>
            <p class="max-w-xs text-sm text-gray-500">
                You can add your own custom statuses to the template status
                dropdown.
            </p>
        </div>
        <div class="mx-auto w-full rounded-md bg-white shadow-card lg:mx-0">
            <div class="flex items-center p-6">
                <div class="flex flex-col flex-wrap gap-6 sm:flex-row">
                    <div
                        class="w-full rounded-md border border-gray-300 bg-white text-sm sm:w-[300px]"
                    >
                        <div
                            v-for="status in statuses"
                            :key="status"
                            class="cursor-pointer border-b border-gray-200"
                        >
                            <div
                                class="group flex cursor-pointer items-center justify-between p-2 text-gray-600 hover:bg-gray-100"
                            >
                                <div class="flex items-center gap-2">
                                    <span class="py-1">{{ status }}</span>
                                    <PencilSquareIcon
                                        class="hidden h-6 w-6 shrink-0 cursor-pointer text-gray-500 hover:text-gray-700 group-hover:block"
                                        @click="editItem(status)"
                                    />
                                </div>
                                <XMarkIcon
                                    class="hidden h-6 w-6 shrink-0 cursor-pointer text-gray-500 hover:text-gray-700 group-hover:block"
                                    @click="deleteItem(status)"
                                />
                            </div>
                        </div>
                        <div
                            class="group flex h-10 cursor-pointer items-center justify-center rounded-b-md hover:bg-gray-100"
                            @click="createItem"
                        >
                            <PlusCircleIcon
                                class="h-6 w-6 text-gray-500 group-hover:text-gray-700"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div class="flex justify-end gap-4 px-6 py-3">
                <LockableButton
                    type="submit"
                    color="yellow"
                    size="sm"
                    :disabled="saveDisabled"
                    :locked="!isAdmin && !isOwner && !isEditor"
                    lock-message="Only the team owner and admin can update the custom statuses."
                >
                    Save changes
                </LockableButton>
            </div>
        </div>

        <CustomStatusesModal
            v-if="showModal"
            :status="selectedStatus"
            @close="handleCloseModal"
            @submit="updateStatuses"
        />
    </form>
</template>
<script lang="ts" setup>
import { PropType } from 'vue';
import {
    XMarkIcon,
    PencilSquareIcon,
    PlusCircleIcon,
} from '@heroicons/vue/20/solid';
import useConfirm from '@composables/useConfirm';

const props = defineProps({
    modelValue: {
        type: Array as PropType<string[]>,
        required: true,
    },
});

const emit = defineEmits(['update:modelValue']);

const { show: showConfirmation } = useConfirm();
const showModal = ref(false);
const selectedStatus = ref('');
const { isAdmin, isOwner, isEditor } = usePermissions();

const handleCloseModal = () => {
    showModal.value = false;
    selectedStatus.value = '';
};

const saveDisabled = computed(() => {
    if (props.modelValue.length !== statuses.value.length) {
        return false;
    }

    return props.modelValue.every(
        (status, index) => status === statuses.value[index]
    );
});

const statuses = ref([...props.modelValue]);

const updateStatuses = (status: string) => {
    const foundDuplicate = statuses.value.find((e) => e === status);
    if (foundDuplicate) {
        handleCloseModal();
        return;
    }

    const index = statuses.value.findIndex((e) => e === selectedStatus.value);

    if (index === -1) {
        statuses.value.push(status);
    } else {
        statuses.value[index] = status;
    }

    handleCloseModal();
};

const updateCustomStatuses = () => {
    emit('update:modelValue', statuses.value);
};

const createItem = () => {
    selectedStatus.value = '';
    showModal.value = true;
};

const editItem = (status: string) => {
    selectedStatus.value = status;
    showModal.value = true;
};

const deleteItem = async (status: string) => {
    const confirmed = await showConfirmation({
        header: 'Delete status',
        message: `Do you really want to delete this ${status}?`,
        type: 'danger',
    });

    if (!confirmed) {
        return;
    }

    statuses.value = statuses.value.filter((e) => e !== status);
};
</script>
